import type { ToolKey } from "./types";

export type FieldType = "text" | "textarea" | "select" | "number" | "date";

export type ToolField = {
  name: string;
  label: string;
  type: FieldType;
  placeholder?: string;
  required?: boolean;
  options?: string[];
  helper?: string;
  rows?: number;
  min?: number;
};

export type ToolSection = { heading: string; fields: ToolField[] };

export type ToolFormConfig = {
  tool: ToolKey;
  title: string;
  blurb: string;
  titleFrom: (input: Record<string, string>) => string;
  /**
   * Static sections used when no dynamic resolver is provided OR as the
   * baseline that `getSections()` returns when there is no live input.
   */
  sections: ToolSection[];
  /**
   * Optional resolver that returns the live form schema based on the
   * user's current answers. Used by the Agreement tool to swap in the
   * essentialia for the chosen agreement type.
   */
  getSections?: (input: Record<string, string>) => ToolSection[];
};

/* -------------------------------------------------------------------------- */
/* AGREEMENT — type-specific essentialia                                       */
/* -------------------------------------------------------------------------- */

const AGREEMENT_TYPES = [
  "Non-Disclosure Agreement (mutual)",
  "Non-Disclosure Agreement (one-way)",
  "Sale of Business",
  "Sale of Shares",
  "Lease Agreement",
  "Employment Contract",
  "Independent Contractor Agreement",
  "Service Level Agreement",
  "Loan Agreement",
  "Shareholders Agreement",
  "Memorandum of Understanding",
] as const;

type AgreementType = (typeof AGREEMENT_TYPES)[number];

/** Common base — type chooser, who you act for, governing law. */
const agreementBase: ToolSection[] = [
  {
    heading: "Type & capacity",
    fields: [
      {
        name: "agreement_type",
        label: "Agreement type",
        type: "select",
        required: true,
        options: AGREEMENT_TYPES as unknown as string[],
        helper: "Selecting a type tailors the rest of the form to the essentialia of that contract.",
      },
      {
        name: "acting_for",
        label: "Which party are you acting for?",
        type: "select",
        required: true,
        options: ["Party A", "Party B", "Both / neutral drafter"],
        helper: "The draft will be balanced but lean protective of this party.",
      },
      {
        name: "governing_law",
        label: "Governing law",
        type: "select",
        options: ["South Africa"],
      },
    ],
  },
  {
    heading: "Parties",
    fields: [
      { name: "party_a", label: "Party A — full legal name", type: "text", required: true, placeholder: "ABC (Pty) Ltd" },
      { name: "party_a_reg", label: "Party A reg / ID number", type: "text" },
      { name: "party_b", label: "Party B — full legal name", type: "text", required: true, placeholder: "XYZ (Pty) Ltd" },
      { name: "party_b_reg", label: "Party B reg / ID number", type: "text" },
    ],
  },
];

/** Type-specific essentialia. Returns the section to append after parties. */
const agreementEssentialia = (type: AgreementType | string): ToolSection[] => {
  switch (type) {
    case "Non-Disclosure Agreement (mutual)":
    case "Non-Disclosure Agreement (one-way)":
      return [
        {
          heading: "Confidentiality terms",
          fields: [
            { name: "purpose", label: "Purpose of disclosure", type: "textarea", rows: 3, required: true, placeholder: "Evaluation of a proposed joint venture in the renewable energy sector." },
            { name: "effective_date", label: "Effective date", type: "date", required: true },
            { name: "duration", label: "Confidentiality period", type: "select", options: ["1 year", "2 years", "3 years", "5 years", "Indefinite"] },
            { name: "carve_outs", label: "Excluded information (optional)", type: "textarea", rows: 2, placeholder: "Information already in the public domain, independently developed information…" },
          ],
        },
      ];

    case "Sale of Business":
      return [
        {
          heading: "Sale essentialia",
          fields: [
            { name: "merx_description", label: "Description of the business (merx)", type: "textarea", rows: 3, required: true, placeholder: "The bakery business conducted under the name 'Doughworks' from premises at 123 Main Rd, Sandton, including goodwill, plant, equipment, stock and contracts." },
            { name: "purchase_price", label: "Purchase price (ZAR)", type: "number", min: 1, required: true, placeholder: "5000000" },
            { name: "payment_terms", label: "Payment terms", type: "select", required: true, options: ["Cash on signature", "Cash on transfer", "Deposit + balance on transfer", "Vendor finance / instalments", "Earn-out structure"] },
            { name: "effective_date", label: "Effective date of sale", type: "date", required: true },
            { name: "assets_included", label: "Assets included", type: "textarea", rows: 3, placeholder: "Stock, equipment, contracts, IP, goodwill, customer database…" },
            { name: "liabilities_assumed", label: "Liabilities assumed by purchaser", type: "textarea", rows: 2, placeholder: "None / accounts payable as at the effective date / specific contracts…" },
            { name: "conditions_precedent", label: "Conditions precedent", type: "textarea", rows: 3, placeholder: "Competition Commission approval, finance approval, due diligence, landlord consent…" },
            { name: "restraint", label: "Restraint of trade?", type: "select", options: ["No", "12 months", "24 months", "36 months", "60 months"] },
          ],
        },
      ];

    case "Sale of Shares":
      return [
        {
          heading: "Share sale essentialia",
          fields: [
            { name: "company_name", label: "Target company", type: "text", required: true, placeholder: "Target Co (Pty) Ltd" },
            { name: "shares_sold", label: "Shares being sold (% of issued share capital)", type: "text", required: true, placeholder: "100% / 51% / 1,000 ordinary shares" },
            { name: "purchase_price", label: "Purchase price (ZAR)", type: "number", min: 1, required: true, placeholder: "10000000" },
            { name: "payment_terms", label: "Payment terms", type: "select", required: true, options: ["Cash on closing", "Deposit + balance on closing", "Vendor finance", "Escrow / deferred"] },
            { name: "effective_date", label: "Closing date", type: "date", required: true },
            { name: "conditions_precedent", label: "Conditions precedent", type: "textarea", rows: 3, placeholder: "Competition Commission, regulatory approvals, board / shareholder approvals…" },
            { name: "warranties_focus", label: "Key warranties to highlight", type: "textarea", rows: 3, placeholder: "Tax, litigation, IP ownership, material contracts, employees…" },
          ],
        },
      ];

    case "Lease Agreement":
      return [
        {
          heading: "Lease essentialia",
          fields: [
            { name: "premises", label: "Description of leased premises", type: "textarea", rows: 2, required: true, placeholder: "Shop 5, Sandton City, 83 Rivonia Road, Sandton, measuring 120m²" },
            { name: "use", label: "Permitted use", type: "text", required: true, placeholder: "Retail clothing store" },
            { name: "rental_amount", label: "Monthly rental (ZAR, excl. VAT)", type: "number", min: 1, required: true, placeholder: "45000" },
            { name: "escalation", label: "Annual escalation (%)", type: "number", min: 0, placeholder: "8" },
            { name: "deposit", label: "Deposit (ZAR)", type: "number", min: 0, placeholder: "90000" },
            { name: "lease_start", label: "Lease commencement", type: "date", required: true },
            { name: "lease_term", label: "Lease term", type: "select", required: true, options: ["6 months", "12 months", "24 months", "36 months", "60 months", "Other"] },
            { name: "renewal_option", label: "Renewal option?", type: "select", options: ["No", "Yes — same term", "Yes — month-to-month thereafter"] },
            { name: "responsibility_repairs", label: "Repairs & maintenance", type: "select", options: ["Lessor responsible", "Lessee responsible", "Split — structural lessor, internal lessee"] },
          ],
        },
      ];

    case "Employment Contract":
      return [
        {
          heading: "Employment essentialia",
          fields: [
            { name: "position", label: "Position / job title", type: "text", required: true, placeholder: "Senior Software Engineer" },
            { name: "reports_to", label: "Reports to", type: "text", placeholder: "Chief Technology Officer" },
            { name: "start_date", label: "Start date", type: "date", required: true },
            { name: "employment_type", label: "Employment type", type: "select", required: true, options: ["Permanent", "Fixed-term", "Probation then permanent"] },
            { name: "probation_period", label: "Probation period", type: "select", options: ["None", "3 months", "6 months"] },
            { name: "remuneration", label: "Total cost-to-company (monthly, ZAR)", type: "number", min: 1, required: true, placeholder: "75000" },
            { name: "benefits", label: "Benefits included", type: "textarea", rows: 2, placeholder: "Medical aid (50% subsidy), pension fund (10% employer), 21 days annual leave…" },
            { name: "working_hours", label: "Working hours", type: "text", placeholder: "Monday–Friday, 08:00–17:00" },
            { name: "notice_period", label: "Notice period", type: "select", options: ["1 week", "1 month", "3 months", "6 months"] },
            { name: "restraint", label: "Restraint of trade", type: "select", options: ["None", "6 months", "12 months", "24 months"] },
          ],
        },
      ];

    case "Independent Contractor Agreement":
      return [
        {
          heading: "Contractor terms",
          fields: [
            { name: "scope", label: "Scope of services", type: "textarea", rows: 4, required: true, placeholder: "Develop and deliver the v1 mobile application per the attached specification…" },
            { name: "deliverables", label: "Deliverables & milestones", type: "textarea", rows: 3, placeholder: "MVP by 30 June; v1 by 30 Sept; handover & training by 15 Oct" },
            { name: "fees", label: "Fees (ZAR, excl. VAT)", type: "text", required: true, placeholder: "R350,000 fixed / R1,200 per hour" },
            { name: "payment_schedule", label: "Payment schedule", type: "select", options: ["50% upfront, 50% on completion", "Monthly arrears", "Per milestone", "30 days after invoice"] },
            { name: "start_date", label: "Start date", type: "date", required: true },
            { name: "duration", label: "Duration", type: "text", placeholder: "6 months / Until completion" },
            { name: "ip_ownership", label: "IP ownership", type: "select", options: ["Vests in client on payment", "Contractor retains, client gets licence", "Joint"] },
          ],
        },
      ];

    case "Service Level Agreement":
      return [
        {
          heading: "Service & SLAs",
          fields: [
            { name: "services", label: "Services provided", type: "textarea", rows: 3, required: true, placeholder: "24/7 cloud hosting, monitoring, backup and incident response for the customer's production environment." },
            { name: "fees", label: "Fees (ZAR per month, excl. VAT)", type: "number", min: 1, required: true, placeholder: "25000" },
            { name: "effective_date", label: "Effective date", type: "date", required: true },
            { name: "duration", label: "Initial term", type: "select", required: true, options: ["12 months", "24 months", "36 months", "Month-to-month"] },
            { name: "uptime_target", label: "Uptime target", type: "select", options: ["99.0%", "99.5%", "99.9%", "99.99%"] },
            { name: "response_time", label: "Critical incident response time", type: "select", options: ["15 min", "30 min", "1 hour", "4 hours"] },
            { name: "service_credits", label: "Service credit regime", type: "textarea", rows: 2, placeholder: "5% credit per 0.1% below uptime target, capped at 25% of monthly fee" },
            { name: "termination_notice", label: "Termination for convenience notice", type: "select", options: ["None", "30 days", "60 days", "90 days"] },
          ],
        },
      ];

    case "Loan Agreement":
      return [
        {
          heading: "Loan terms",
          fields: [
            { name: "loan_amount", label: "Loan amount (ZAR)", type: "number", min: 1, required: true, placeholder: "1000000" },
            { name: "purpose", label: "Purpose of loan", type: "text", placeholder: "Working capital / property acquisition" },
            { name: "interest_rate", label: "Interest rate", type: "text", required: true, placeholder: "Prime + 2% / 12% NACM / 0% interest-free" },
            { name: "drawdown_date", label: "Drawdown date", type: "date", required: true },
            { name: "repayment_terms", label: "Repayment", type: "select", required: true, options: ["Bullet on maturity", "Equal monthly instalments", "Interest-only with balloon", "On demand"] },
            { name: "repayment_period", label: "Repayment period", type: "select", required: true, options: ["6 months", "12 months", "24 months", "36 months", "60 months", "On demand"] },
            { name: "security", label: "Security / suretyships", type: "textarea", rows: 2, placeholder: "Personal suretyship of the directors / mortgage bond over erf 123 / cession of book debts" },
            { name: "events_of_default", label: "Specific events of default", type: "textarea", rows: 2, placeholder: "Failure to pay, insolvency, change of control, material breach…" },
          ],
        },
      ];

    case "Shareholders Agreement":
      return [
        {
          heading: "Shareholders' agreement essentialia",
          fields: [
            { name: "company_name", label: "Company", type: "text", required: true, placeholder: "NewCo (Pty) Ltd" },
            { name: "shareholding", label: "Shareholding split", type: "textarea", rows: 2, required: true, placeholder: "Party A — 60%; Party B — 40%" },
            { name: "board_composition", label: "Board composition & quorum", type: "textarea", rows: 2, placeholder: "3 directors: 2 appointed by A, 1 by B. Quorum = 2 including 1 of each." },
            { name: "reserved_matters", label: "Reserved matters (require unanimous / supermajority)", type: "textarea", rows: 3, placeholder: "Issuing shares, incurring debt > R1m, sale of assets, change of business, dividend policy…" },
            { name: "dividend_policy", label: "Dividend policy", type: "select", options: ["Discretionary", "Mandatory once retained earnings exceed threshold", "Fixed % of profits"] },
            { name: "transfer_restrictions", label: "Transfer mechanics", type: "select", options: ["Pre-emptive rights (s39 Companies Act)", "Tag-along and drag-along", "Right of first refusal", "Lock-in for fixed period"] },
            {
              name: "deadlock_resolution",
              label: "Deadlock mechanism",
              type: "select",
              options: [
                "Escalation to shareholder representatives, then mediation",
                "Mediation under AFSA rules, failing which arbitration",
                "Arbitration under AFSA rules (Arbitration Act 42 of 1965)",
                "Independent expert determination (binding)",
                "Casting vote to chairperson",
                "Buy-sell at fair value (independent valuation)",
                "Winding-up on just and equitable grounds (s81(1)(d) Companies Act)",
              ],
            },
            { name: "exit_strategy", label: "Exit strategy", type: "textarea", rows: 2, placeholder: "Trade sale or IPO targeted within 5–7 years; buy-out option for minority after 3 years…" },
          ],
        },
      ];

    case "Memorandum of Understanding":
      return [
        {
          heading: "MOU terms",
          fields: [
            { name: "purpose", label: "Purpose & objectives", type: "textarea", rows: 4, required: true, placeholder: "To set out the parties' intent to collaborate on…" },
            { name: "effective_date", label: "Effective date", type: "date", required: true },
            { name: "duration", label: "Duration", type: "text", placeholder: "6 months / until definitive agreements signed" },
            { name: "binding", label: "Binding effect", type: "select", required: true, options: ["Non-binding except for confidentiality and exclusivity", "Fully binding", "Binding only on specified clauses"] },
            { name: "exclusivity", label: "Exclusivity period", type: "select", options: ["None", "30 days", "60 days", "90 days", "180 days"] },
            { name: "next_steps", label: "Next steps & responsibilities", type: "textarea", rows: 2, placeholder: "Party A to provide information memorandum within 14 days; Party B to complete DD within 60 days…" },
          ],
        },
      ];

    default:
      return [
        {
          heading: "Commercial terms",
          fields: [
            { name: "effective_date", label: "Effective date", type: "date", required: true },
            { name: "duration", label: "Duration", type: "text", placeholder: "12 months / Indefinite" },
            { name: "key_terms", label: "Key commercial terms", type: "textarea", rows: 5, required: true, placeholder: "Set out the essentialia: scope, fees, deliverables…" },
          ],
        },
      ];
  }
};

/* -------------------------------------------------------------------------- */
/* TOOL CONFIGS                                                                */
/* -------------------------------------------------------------------------- */

export const TOOL_FORMS: Record<ToolKey, ToolFormConfig> = {
  affidavit: {
    tool: "affidavit",
    title: "Affidavit Drafter",
    blurb:
      "Sworn statement for South African proceedings. Includes oath formula and commissioner of oaths jurat block.",
    titleFrom: (i) => `Affidavit — ${i.deponent_name || "Untitled deponent"}`,
    sections: [
      {
        heading: "Deponent",
        fields: [
          { name: "deponent_name", label: "Full name", type: "text", required: true, placeholder: "Jane Doe" },
          { name: "deponent_id", label: "Identity / passport no.", type: "text", placeholder: "8001015009087" },
          { name: "deponent_capacity", label: "Capacity", type: "text", placeholder: "Director / Applicant / Respondent / Plaintiff / Defendant / Witness" },
          { name: "deponent_address", label: "Residential address", type: "textarea", rows: 2, placeholder: "Street, suburb, city" },
        ],
      },
      {
        heading: "Matter",
        fields: [
          {
            name: "matter_court",
            label: "Court",
            type: "select",
            options: [
              "High Court — Gauteng (Pretoria)",
              "High Court — Gauteng (Johannesburg)",
              "High Court — Western Cape",
              "High Court — KwaZulu-Natal",
              "Magistrates' Court",
              "Labour Court",
              "Other / N/A",
            ],
          },
          { name: "matter_case_number", label: "Case number", type: "text", placeholder: "12345/2026" },
          { name: "matter_parties", label: "Parties", type: "text", placeholder: "ABC (Pty) Ltd v XYZ" },
        ],
      },
      {
        heading: "Facts",
        fields: [
          {
            name: "facts",
            label: "Facts to depose to",
            type: "textarea",
            required: true,
            rows: 8,
            placeholder: "Set out the facts using plain language where appropriate. Numbered paragraphs will be generated automatically.",
            helper: "Each distinct fact will become its own numbered paragraph in the draft.",
          },
        ],
      },
    ],
  },

  lod: {
    tool: "lod",
    title: "Letter of Demand",
    blurb:
      "Formal demand with automatic mora interest under the Prescribed Rate of Interest Act 55 of 1975.",
    titleFrom: (i) => `LOD — ${i.debtor_name || "Untitled debtor"}`,
    sections: [
      {
        heading: "Acting for",
        fields: [
          {
            name: "acting_for",
            label: "Which side are you acting for?",
            type: "select",
            required: true,
            options: ["Creditor (sending demand)", "Debtor (responding — draft a reply / dispute)"],
            helper: "Tone and structure adapt to whether you are demanding payment or responding to a demand.",
          },
        ],
      },
      {
        heading: "Creditor",
        fields: [
          { name: "creditor_name", label: "Creditor name", type: "text", required: true, placeholder: "ABC (Pty) Ltd" },
          { name: "creditor_address", label: "Address", type: "textarea", rows: 2 },
        ],
      },
      {
        heading: "Debtor",
        fields: [
          { name: "debtor_name", label: "Debtor name", type: "text", required: true, placeholder: "John Smith" },
          { name: "debtor_address", label: "Address", type: "textarea", rows: 2 },
        ],
      },
      {
        heading: "Debt",
        fields: [
          { name: "amount", label: "Capital amount (ZAR)", type: "number", required: true, min: 1, placeholder: "150000" },
          { name: "due_date", label: "Date amount became due", type: "date", required: true },
          { name: "basis", label: "Basis of debt", type: "textarea", rows: 3, placeholder: "Invoice 4521 dated 15 March 2026 for consulting services rendered…" },
          {
            name: "consequence",
            label: "Consequence on non-payment",
            type: "select",
            options: ["Summons", "Liquidation application", "Sequestration", "Arbitration"],
          },
          { name: "demand_days", label: "Days to comply", type: "number", min: 1, placeholder: "7" },
        ],
      },
    ],
  },

  resolution: {
    tool: "resolution",
    title: "Resolution Drafter / Reviewer",
    blurb:
      "Board, shareholder, written or special resolutions compliant with the Companies Act 71 of 2008.",
    titleFrom: (i) => `${i.resolution_type || "Resolution"} — ${i.company_name || "Untitled"}`,
    sections: [
      {
        heading: "Company",
        fields: [
          { name: "company_name", label: "Company name", type: "text", required: true, placeholder: "ABC (Pty) Ltd" },
          { name: "company_reg", label: "Registration number", type: "text", placeholder: "2018/123456/07" },
        ],
      },
      {
        heading: "Resolution",
        fields: [
          {
            name: "resolution_type",
            label: "Type",
            type: "select",
            required: true,
            options: ["Board resolution", "Written resolution (s60)", "Ordinary shareholders' resolution", "Special resolution"],
          },
          { name: "meeting_date", label: "Meeting / signing date", type: "date", required: true },
          { name: "chairperson", label: "Chairperson", type: "text", placeholder: "Name of chair" },
          {
            name: "subject",
            label: "Subject matter",
            type: "textarea",
            required: true,
            rows: 5,
            placeholder: "Approval of acquisition of property at erf 123, Sandton, for R5,000,000…",
            helper: "Voting thresholds will be applied automatically to match the resolution type.",
          },
        ],
      },
    ],
  },

  agreement: {
    tool: "agreement",
    title: "Agreement Drafter / Reviewer",
    blurb:
      "Pick the agreement type and the form will adapt to ask only the essentialia for that contract. Drafted by Casey against your firm's rubric.",
    titleFrom: (i) => `${i.agreement_type || "Agreement"} — ${i.party_a || ""}${i.party_b ? " & " + i.party_b : ""}`,
    sections: agreementBase, // baseline shown before a type is chosen
    getSections: (input) => {
      const type = input.agreement_type ?? "";
      if (!type) return agreementBase;
      return [...agreementBase, ...agreementEssentialia(type)];
    },
  },

  dd: {
    tool: "dd",
    title: "Due Diligence",
    blurb:
      "Generate a structured due diligence checklist for an M&A or project finance transaction.",
    titleFrom: (i) => `Due Diligence — ${i.target_name || "Untitled target"}`,
    sections: [
      {
        heading: "Transaction",
        fields: [
          {
            name: "transaction_type",
            label: "Transaction type",
            type: "select",
            required: true,
            options: [
              "Share acquisition",
              "Asset acquisition",
              "Merger",
              "Project finance",
              "Joint venture",
              "Private equity investment",
            ],
          },
          { name: "target_name", label: "Target / counterparty", type: "text", required: true, placeholder: "ABC (Pty) Ltd" },
          { name: "target_reg", label: "Target reg number", type: "text", placeholder: "2018/123456/07" },
          {
            name: "industry",
            label: "Industry",
            type: "select",
            options: [
              "Mining & resources",
              "Financial services",
              "Telecommunications",
              "Energy & utilities",
              "Manufacturing",
              "Retail & consumer",
              "Healthcare",
              "Technology",
              "Real estate",
              "Other",
            ],
          },
        ],
      },
      {
        heading: "Scope",
        fields: [
          { name: "deal_value", label: "Estimated deal value (ZAR)", type: "number", min: 1, placeholder: "50000000" },
          { name: "target_close", label: "Target closing date", type: "date" },
          {
            name: "focus_areas",
            label: "Focus areas",
            type: "textarea",
            rows: 5,
            required: true,
            placeholder: "Corporate, contracts, litigation, IP, employment, BBBEE, regulatory, tax, environmental…",
            helper: "List the workstreams to include in the DD checklist.",
          },
        ],
      },
    ],
  },

  cp: {
    tool: "cp",
    title: "Conditions Precedent Tracker",
    blurb:
      "Casey reads an agreement, extracts every condition precedent, and works out each deadline (court days versus calendar days) so nothing lapses.",
    titleFrom: (i) => `CP Tracker — ${i.agreement_name || "Untitled agreement"}`,
    sections: [
      {
        heading: "Agreement",
        fields: [
          {
            name: "agreement_name",
            label: "Agreement name or reference",
            type: "text",
            required: true,
            placeholder: "Share Sale Agreement — Project Falcon",
          },
          { name: "signature_date", label: "Signature / effective date", type: "date", required: true },
          {
            name: "cp_clauses",
            label: "Conditions precedent clause(s)",
            type: "textarea",
            required: true,
            rows: 8,
            placeholder: "Paste the conditions precedent clause(s) from the agreement. Document upload arrives with launch.",
            helper: "Casey extracts each condition precedent and computes its deadline from this text.",
          },
        ],
      },
      {
        heading: "Time computation",
        fields: [
          {
            name: "day_basis",
            label: "How does the agreement count days?",
            type: "select",
            required: true,
            options: [
              "As defined in the agreement",
              "Court days (exclude weekends and public holidays)",
              "Calendar days",
              "Business days",
            ],
            helper: "Where the agreement is silent, Casey applies the general South African computation-of-time rules and records the basis used.",
          },
        ],
      },
    ],
  },

  compliance: {
    tool: "compliance",
    title: "Compliance Agent",
    blurb:
      "Track an obligation across POPIA, FICA, B-BBEE, Companies Act and more. Automated overdue alerts.",
    titleFrom: (i) => `${i.statute || "Compliance"} — ${i.obligation || "Obligation"}`,
    sections: [
      {
        heading: "Obligation",
        fields: [
          {
            name: "statute",
            label: "Statute",
            type: "select",
            required: true,
            options: [
              "POPIA",
              "FICA",
              "B-BBEE",
              "Companies Act 71 of 2008",
              "Labour Relations Act",
              "Employment Equity Act",
              "Competition Act",
              "JSE Listings Requirements",
            ],
          },
          { name: "obligation", label: "Obligation", type: "text", required: true, placeholder: "Annual PAIA manual review" },
          { name: "owner", label: "Responsible person", type: "text", placeholder: "Compliance officer" },
          { name: "due_date", label: "Next due date", type: "date", required: true },
          {
            name: "frequency",
            label: "Frequency",
            type: "select",
            options: ["Once-off", "Monthly", "Quarterly", "Bi-annually", "Annually"],
          },
          { name: "notes", label: "Notes", type: "textarea", rows: 3 },
        ],
      },
    ],
  },
};

/** Helper used by the modal to resolve sections from current form values. */
export const resolveSections = (
  config: ToolFormConfig,
  input: Record<string, string>
): ToolSection[] => (config.getSections ? config.getSections(input) : config.sections);
